import React, { useState, useEffect } from 'react';
import { Award, Edit2, Save, X, Sparkles, Star, CheckCircle2, Crown, Shield, Heart, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Celey Balloon Icon
const CeleyBalloon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 3.17 2.11 5.85 5 6.71V17c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-1.29c2.89-.86 5-3.54 5-6.71 0-3.87-3.13-7-7-7z" />
    <path d="M11 18h2v1h-2z" />
    <path d="M12 19c-.28 0-.5.22-.5.5v2c0 .28.22.5.5.5s.5-.22.5-.5v-2c0-.28-.22-.5-.5-.5z" />
    <ellipse cx="9" cy="7" rx="1.5" ry="2" fill="white" opacity="0.3" />
  </svg>
);

// Icon options for badges
const iconOptions = [
  { value: 'balloon', label: 'Balloon', icon: CeleyBalloon },
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'check-circle', label: 'Check Circle', icon: CheckCircle2 },
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'zap', label: 'Zap', icon: Zap },
];

// Color options for badges
const colorOptions = [
  { value: 'founding', label: 'Founding (Gray + Green)', preview: 'bg-gray-50 border-gray-200 text-emerald-600' },
  { value: 'amber', label: 'Amber', preview: 'bg-amber-50 border-amber-200 text-amber-600' },
  { value: 'yellow', label: 'Yellow', preview: 'bg-yellow-50 border-yellow-200 text-yellow-600' },
  { value: 'emerald', label: 'Emerald', preview: 'bg-emerald-50 border-emerald-200 text-emerald-600' },
  { value: 'blue', label: 'Blue', preview: 'bg-blue-50 border-blue-200 text-blue-600' },
  { value: 'purple', label: 'Purple', preview: 'bg-purple-50 border-purple-200 text-purple-600' },
  { value: 'rose', label: 'Rose', preview: 'bg-rose-50 border-rose-200 text-rose-600' },
];

const AdminBadgesTab = () => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBadge, setEditingBadge] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/badges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBadges(data);
      } else {
        toast.error('Failed to fetch badges');
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
      toast.error('Error loading badges');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (badge) => {
    setEditingBadge(badge.id);
    setEditForm({
      label: badge.label,
      description: badge.description,
      icon: badge.icon,
      color: badge.color,
      applicable_to: badge.applicable_to,
      active: badge.active
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/badges/${editingBadge}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Badge updated successfully');
        fetchBadges();
        setEditingBadge(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update badge');
      }
    } catch (error) {
      console.error('Error updating badge:', error);
      toast.error('Error saving badge');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingBadge(null);
    setEditForm({});
  };

  const getIconComponent = (iconName) => {
    const option = iconOptions.find(o => o.value === iconName);
    return option ? option.icon : Sparkles;
  };

  const getColorPreview = (colorName) => {
    const option = colorOptions.find(o => o.value === colorName);
    return option ? option.preview : 'bg-gray-50 border-gray-200 text-gray-600';
  };

  if (loading) {
    return <div className="p-8 text-center">Loading badges...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Badge Definitions
          </CardTitle>
          <CardDescription>
            Manage badge types and their appearance. Changes affect how badges display across the platform.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Icon</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Applicable To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {badges.map((badge) => {
                const IconComponent = getIconComponent(badge.icon);
                const colorPreview = getColorPreview(badge.color);
                const isEditing = editingBadge === badge.id;
                
                return (
                  <TableRow key={badge.id}>
                    <TableCell>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${colorPreview}`}>
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm font-medium">{badge.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{badge.label}</TableCell>
                    <TableCell>{badge.icon}</TableCell>
                    <TableCell>{badge.color}</TableCell>
                    <TableCell className="capitalize">{badge.applicable_to}</TableCell>
                    <TableCell>
                      <Badge variant={badge.active ? "default" : "secondary"}>
                        {badge.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(badge)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Badge Dialog */}
      <Dialog open={!!editingBadge} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Badge</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border ${getColorPreview(editForm.color)}`}>
                {(() => {
                  const IconComponent = getIconComponent(editForm.icon);
                  return <IconComponent className="h-4 w-4" />;
                })()}
                <span className="text-sm font-semibold">{editForm.label || 'Badge'}</span>
              </div>
            </div>

            {/* Label */}
            <div>
              <Label>Label</Label>
              <Input
                value={editForm.label || ''}
                onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Input
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Icon */}
            <div>
              <Label>Icon</Label>
              <Select
                value={editForm.icon}
                onValueChange={(value) => setEditForm({ ...editForm, icon: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((option) => {
                    const IconComp = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <IconComp className="h-4 w-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Color */}
            <div>
              <Label>Color Scheme</Label>
              <Select
                value={editForm.color}
                onValueChange={(value) => setEditForm({ ...editForm, color: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${option.preview.split(' ')[0]}`} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Applicable To */}
            <div>
              <Label>Applicable To</Label>
              <Select
                value={editForm.applicable_to}
                onValueChange={(value) => setEditForm({ ...editForm, applicable_to: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="provider">Providers Only</SelectItem>
                  <SelectItem value="customer">Customers Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBadgesTab;
